import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';

interface UserFunction {
    functie_id: number;
    functie_name: string;
    is_active: boolean;
    functie_active_auth_core: boolean;
}

@Component({
    selector: 'app-functie-selection-dialog',
    templateUrl: './functie-selection-dialog.component.html',
    styleUrls: ['./functie-selection-dialog.component.scss']
})
export class FunctieSelectionDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    selectedFunctionId: number | null = null;
    isSubmitting = false;

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    successIcon: string = environment.config.customNotifications.icons.success;
    helper = helper;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { user: any; functions: UserFunction[] },
        private dialogRef: MatDialogRef<FunctieSelectionDialogComponent>,
        private operatoriService: OperatoriService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        // Pre-select the currently active function if available
        if (this.data.functions && this.data.functions.length > 0) {
            const activeFunction = this.data.functions.find(func => func.is_active);
            if (activeFunction) {
                this.selectedFunctionId = activeFunction.functie_id;
            } else {
                // If no active function, select the first one
                this.selectedFunctionId = this.data.functions[0].functie_id;
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onFunctionSelect(functieId: number): void {
        this.selectedFunctionId = functieId;
    }

    onConfirm(): void {
        if (!this.selectedFunctionId) {
            this.notificationService.warningSwal(
                'Atenție',
                'Trebuie să selectați o funcție pentru a continua',
                this.errorIcon
            );
            return;
        }

        this.isSubmitting = true;

        this.operatoriService.setActiveFunctie(this.data.user.id_user, this.selectedFunctionId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isSubmitting = false;
                })
            )
            .subscribe({
                next: (response) => {
                    const selectedFunction = this.data.functions.find(f => f.functie_id === this.selectedFunctionId);

                    this.notificationService.warningSwal(
                        'Succes',
                        `Funcția activă a fost setată la: ${selectedFunction?.functie_name}`,
                        this.successIcon
                    );

                    this.dialogRef.close({ success: true, selectedFunctionId: this.selectedFunctionId });

                    // Reload the page to refresh permissions and data
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                },
                error: (error) => {
                    console.error('Error setting active function:', error);

                    let errorMessage = 'Eroare la setarea funcției active';
                    try {
                        errorMessage = error.error?.message || errorMessage;
                    } catch (e) {
                        // Use default message
                    }

                    this.notificationService.warningSwal(
                        this.errorTitle,
                        errorMessage,
                        this.errorIcon
                    );
                }
            });
    }

    onCancel(): void {
        // Only allow cancel if this is not a required selection
        // (i.e., user has some functions but just no active one)
        if (this.hasAtLeastOneFunction()) {
            this.dialogRef.close({ success: false });
        }
    }

    canCancel(): boolean {
        // Can cancel if user has at least one function (meaning they can work without setting active)
        // Cannot cancel if user has no functions at all
        return this.hasAtLeastOneFunction();
    }

    hasAtLeastOneFunction(): boolean {
        return this.data.functions && this.data.functions.length > 0;
    }

    getDialogTitle(): string {
        if (!this.hasAtLeastOneFunction()) {
            return 'Nicio funcție disponibilă';
        }
        return 'Selectați funcția activă';
    }

    getDialogMessage(): string {
        if (!this.hasAtLeastOneFunction()) {
            return 'Nu aveți nicio funcție alocată. Contactați administratorul pentru a vă asigna funcții.';
        }
        return 'Selectați care funcție doriți să fie activă în mod implicit. Puteți schimba funcția activă oricând din bara de navigare.';
    }
}